import { FC } from 'react'
import { AnalyticsFetchTopSourcesResponseItem } from 'src/services/__generated__/api'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import { ReactComponent as EarthSvg } from 'src/assets/svg/earth.svg'
import { Skeleton } from 'src/components/Skeleton'
import { numberWithCommas } from 'src/utils/numberFormat'
import styles from './platformBreakdownView.module.scss'

// .card is 272x168 with a 12px radius, in a 4-column grid
// (platformBreakdownView.module.scss) — one full row of placeholders.
const CARD_HEIGHT = 168
const CARD_RADIUS = 12
const CARD_SKELETON_COUNT = 4

interface PlatformBreakdownViewProps {
  isLoading?: boolean
  overallMentions?: number
  sources?: AnalyticsFetchTopSourcesResponseItem[]
}

interface PlatformBadge {
  label: string
  type: 'purple' | 'orange' | 'gray'
}

const getBadges = (
  item: AnalyticsFetchTopSourcesResponseItem,
  allItems: AnalyticsFetchTopSourcesResponseItem[]
): PlatformBadge[] => {
  const badges: PlatformBadge[] = []
  const total = item.total ?? 0
  const positivePct = item.positivePercentage ?? 0
  const negativePct = item.negativePercentage ?? 0
  const neutralPct = item.neutralPercentage ?? 0

  const maxTotal = Math.max(...allItems.map((s) => s.total ?? 0))
  const maxNeg = Math.max(...allItems.map((s) => s.negatives ?? 0))
  const maxPos = Math.max(...allItems.map((s) => s.positives ?? 0))

  if (total === maxTotal) badges.push({ label: 'Top platform', type: 'purple' })
  if ((item.negatives ?? 0) === maxNeg && maxNeg > 0)
    badges.push({ label: 'Highest neg. volume', type: 'orange' })
  if ((item.positives ?? 0) === maxPos && maxPos > 0)
    badges.push({ label: 'Most positive source', type: 'purple' })
  if (neutralPct > 60) badges.push({ label: 'Most neutral tone', type: 'gray' })
  if (positivePct > 60)
    badges.push({ label: 'Best sentiment score', type: 'purple' })
  if (negativePct > 50)
    badges.push({ label: 'Most negative source', type: 'orange' })

  return badges.slice(0, 2)
}

export const PlatformBreakdownView: FC<PlatformBreakdownViewProps> = ({
  isLoading = false,
  sources = [],
  overallMentions = 0,
}) => {
  // Checked before the empty state: `sources` is [] while the request is in
  // flight, so the view used to announce "No platform data available" for data
  // that was simply still on the way — and the section then jumped from one
  // short line of text to a 168px row of cards when it landed.
  if (isLoading) {
    return (
      <div className={styles.grid}>
        {Array.from({ length: CARD_SKELETON_COUNT }).map((_, index) => (
          <Skeleton
            height={CARD_HEIGHT}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            radius={CARD_RADIUS}
          />
        ))}
      </div>
    )
  }

  if (!sources.length) {
    return (
      <div className={styles.grid}>
        <div
          style={{
            color: 'rgba(37,38,45,0.4)',
            fontSize: 14,
            padding: '16px 0',
          }}
        >
          No platform data available
        </div>
      </div>
    )
  }

  return (
    <div className={styles.grid}>
      {sources.map((item) => {
        const name = item.name ?? ''
        const total = item.total ?? 0
        const positives = item.positives ?? 0
        const negatives = item.negatives ?? 0
        const neutrals = item.neutrals ?? 0
        const posPct = Math.round(item.positivePercentage ?? 0)
        const negPct = Math.round(item.negativePercentage ?? 0)
        const neuPct = Math.round(item.neutralPercentage ?? 0)

        const pctOfTotal =
          overallMentions > 0 ? Math.round((total / overallMentions) * 100) : 0

        const icon = SOCIAL_ICONS[name.toLowerCase()] ?? <EarthSvg />
        const badges = getBadges(item, sources)

        return (
          <div className={styles.card} key={name}>
            {/* Header */}
            <div className={styles.cardHeader}>
              <div className={styles.platformLeft}>
                <span className={styles.platformIcon}>{icon}</span>
                <span className={styles.platformName}>
                  {platformDisplayLabel(
                    name,
                    name.charAt(0).toUpperCase() + name.slice(1)
                  )}
                </span>
              </div>
              <div className={styles.platformRight}>
                <span className={styles.totalCount}>
                  {numberWithCommas(total)}
                </span>
                <span className={styles.totalPct}>{pctOfTotal}% of total</span>
              </div>
            </div>

            {/* Sentiment bar */}
            <div className={styles.bar}>
              <div className={styles.barPos} style={{ width: `${posPct}%` }} />
              <div className={styles.barNeu} style={{ width: `${neuPct}%` }} />
              <div className={styles.barNeg} style={{ width: `${negPct}%` }} />
            </div>

            {/* Mini stat cards */}
            <div className={styles.miniCards}>
              <div className={styles.miniCard}>
                <span className={`${styles.miniValue} ${styles.miniValuePos}`}>
                  {numberWithCommas(positives)}
                </span>
                <span className={styles.miniLabel}>Positive {posPct}%</span>
              </div>
              <div className={styles.miniCard}>
                <span className={styles.miniValue}>
                  {numberWithCommas(neutrals)}
                </span>
                <span className={styles.miniLabel}>Neutral {neuPct}%</span>
              </div>
              <div className={styles.miniCard}>
                <span className={`${styles.miniValue} ${styles.miniValueNeg}`}>
                  {numberWithCommas(negatives)}
                </span>
                <span className={styles.miniLabel}>Negative {negPct}%</span>
              </div>
            </div>

            {/* Badges */}
            {badges.length > 0 && (
              <div className={styles.pills}>
                {badges.map((b) => (
                  <span
                    className={`${styles.pill} ${
                      b.type === 'purple'
                        ? styles.pillPurple
                        : b.type === 'orange'
                          ? styles.pillOrange
                          : styles.pillGray
                    }`}
                    key={b.label}
                  >
                    {b.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
